import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetchEmployees, fetchEmployee, type EmployeeListParams } from './employeeApi';
import { mockDelay } from '@/shared/api/mockDelay';
import { getEmployeeActivityLog } from './activityMock';
import { employees as mockEmployees } from './mockData';

/** GET /v1/employees — список сотрудников с фильтрами и cursor-пагинацией. */
export function useEmployees(params: EmployeeListParams = {}) {
  const baseParams = { ...params };
  delete baseParams.page_token;
  return useInfiniteQuery({
    queryKey: ['employees', baseParams],
    queryFn: ({ pageParam }) => fetchEmployees({ ...baseParams, page_token: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
  });
}

/** GET /v1/employees/:id — карточка сотрудника (базовый профиль). */
export function useEmployee(id: string | null) {
  return useQuery({
    queryKey: ['employees', 'detail', id],
    queryFn: () => fetchEmployee(id!),
    enabled: !!id,
  });
}

/**
 * Доменные данные сотрудника (медосмотры, обучение, СОУТ, риски, СИЗ) —
 * пока мок, до интеграции доменных API из соответствующих эпиков.
 */
export function useEmployeeDomainData(employeeId: string | null) {
  return useQuery({
    queryKey: ['employees', 'domain', employeeId],
    queryFn: () => {
      const numId = Number(employeeId);
      const emp = mockEmployees.find((e) => e.id === numId);
      if (!emp) return mockDelay(null);
      return mockDelay({
        medical: emp.medical,
        training: emp.training,
        internship: emp.internship,
        sout: emp.sout,
        riskAssessment: emp.riskAssessment,
        ppe: emp.ppe,
        workplaceId: emp.workplaceId,
        workConditionClass: emp.workConditionClass,
        hireDate: emp.hireDate,
        unit: emp.unit,
      });
    },
    enabled: !!employeeId,
  });
}

export type EmployeeDomainData = NonNullable<ReturnType<typeof useEmployeeDomainData>['data']>;

/** GET /api/employees/:id/activity — журнал действий (мок). */
export function useEmployeeActivityLog(employeeId: string | null) {
  return useQuery({
    queryKey: ['employees', employeeId, 'activity'],
    queryFn: () => {
      const numId = Number(employeeId);
      return mockDelay(getEmployeeActivityLog(numId));
    },
    enabled: !!employeeId,
  });
}

/**
 * Legacy-хуки для страниц доменов (Medical, Training, Sout, Risks и др.),
 * которые пока используют мок-данные Employee с доменными полями.
 * Будут удалены по мере интеграции доменных API.
 */
export function useEmployeesMock(search?: string) {
  return useQuery({
    queryKey: ['employees-mock', search],
    queryFn: () =>
      mockDelay(
        search ? mockEmployees.filter((e) => e.name.toLowerCase().includes(search.toLowerCase())) : mockEmployees,
      ),
  });
}

export function useEmployeeMock(id: number) {
  return useQuery({
    queryKey: ['employees-mock', 'detail', id],
    queryFn: () => mockDelay(mockEmployees.find((e) => e.id === id)),
    enabled: id > 0,
  });
}
